import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { useGetShippingScheduleQuery, fetchBackgroundData, refreshBackgroundQuery } from '../../redux/api/portalApi';
import { hasShippingPackDue } from '../../utils/shippingEligibility';
import useWeekCalculations from '../../functions/useWeekCalculations';
import { groupJobsByShipDate } from '../JobSchedulePage/utils/tableUtils';
import { normalizeShippingSchedule } from './shippingConfig';
import { formatAssemblyDate, getAssemblyRecordProjectId, getPackingListStatusClass, getProjectStatusClass, normalizeAssemblyRecords, normalizeAssemblyScheduleRows } from '../AssemblyPage/assemblyScheduleConfig';
import '../AssemblyPage/assembly.css';
import './shipping.css';
import useShippingLiveUpdates from './useShippingLiveUpdates';

export const PACKING_LIST_COLUMNS = [
  ['JobNumber', 'Job Number'], ['ModelNumber', 'Model Number'], ['ProjectName', 'Project Name'],
  ['AssemblyProjectStatus', 'Assembly Status'], ['PackingListStatus', 'Packing List Status'],
  ['CurrentPlanShipDate', 'Current Plan Ship Date'], ['Destination', 'Location'], ['Notes', 'Notes'],
];

export default function PackingListStatus() {
  const dispatch = useDispatch();
  const urls = useSelector(selectApiUrls);
  const schedule = useGetShippingScheduleQuery({ url: urls.fullSchedule }, { skip: !urls.fullSchedule, refetchOnMountOrArgChange: true });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [assemblyStatus, setAssemblyStatus] = useState('');
  const [packingStatus, setPackingStatus] = useState('');
  const [weeksToShow, setWeeksToShow] = useState(3);
  const { weekColumns } = useWeekCalculations(52);
  const loadWorkflow = useCallback(async (isActive = () => true, signal, background = false) => {
    if (!background) { setLoading(true); setError(''); }
    try {
      if (!urls.assemblySchedule) throw new Error('Assembly Schedule API is not configured.');
      const loadPage = (page) => {
        const args = {
        url: `${urls.assemblySchedule}${urls.assemblySchedule.includes('?') ? '&' : '?'}page=${page}&limit=1000`,
        contract: { response: { envelope: [], includeMappedKeys: false } },
        };
        return background ? dispatch(fetchBackgroundData({ ...args, signal })) : dispatch(fetchData(args)).unwrap();
      };
      const first = await loadPage(1);
      const totalPages = Math.max(1, Number(first?.pagination?.totalPages) || 1);
      if (!Number.isSafeInteger(totalPages) || totalPages > 1000) throw new Error('Invalid assembly page count');
      const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, index) => loadPage(index + 2)));
      if (!isActive() || signal?.aborted) return false;
      setRecords(normalizeAssemblyRecords([first, ...rest].flatMap(normalizeAssemblyScheduleRows)));
      return true;
    } catch (failure) {
      if (!background && isActive()) setError(failure?.message || 'Unable to load packing list statuses.');
      return false;
    } finally { if (!background && isActive()) setLoading(false); }
  }, [dispatch, urls.assemblySchedule]);
  useEffect(() => {
    let active = true;
    loadWorkflow(() => active);
    return () => { active = false; };
  }, [loadWorkflow]);
  const rows = useMemo(() => {
    const byProject = new Map(records.map((record) => [String(getAssemblyRecordProjectId(record)), record]));
    return normalizeShippingSchedule(schedule.data).filter(hasShippingPackDue).map((job) => {
      const workflow = byProject.get(String(job.ProjectId));
      return { ...job, AssemblyProjectStatus: workflow?.AssemblyProjectStatus ?? '', PackingListStatus: workflow?.PackingListStatus ?? '' };
    }).sort((a, b) => String(a.CurrentPlanShipDate || '9999').localeCompare(String(b.CurrentPlanShipDate || '9999')) || String(a.JobNumber).localeCompare(String(b.JobNumber)));
  }, [schedule.data, records]);
  const filtered = rows.filter((row) => (!assemblyStatus || row.AssemblyProjectStatus === assemblyStatus)
    && (!packingStatus || row.PackingListStatus === packingStatus)
    && PACKING_LIST_COLUMNS.some(([key]) => String(row[key] ?? '').toLowerCase().includes(search.trim().toLowerCase())));
  const grouped = groupJobsByShipDate(filtered, weekColumns);
  const scheduledWeeks = Object.keys(grouped).filter((week) => /^Week \d+$/.test(week) && grouped[week].length)
    .sort((a, b) => Number(a.replace('Week ', '')) - Number(b.replace('Week ', ''))).slice(0, weeksToShow);
  const visibleWeeks = [...(grouped['Past Due']?.length ? ['Past Due'] : []), ...scheduledWeeks];
  const displayedCount = visibleWeeks.reduce((total, week) => total + grouped[week].length, 0);
  const busy = loading || schedule.isFetching;
  const live = useShippingLiveUpdates({ enabled: true, scope: urls.shipping, blocked: busy,
    refresh: async ({ signal } = {}) => {
      if (!urls.fullSchedule) return false;
      const [workflowLoaded, result] = await Promise.all([loadWorkflow(() => !signal?.aborted, signal, true),
        dispatch(refreshBackgroundQuery('getShippingSchedule', { url: urls.fullSchedule }, { signal }))]);
      return workflowLoaded && result;
    } });
  const failed = error || schedule.error || !urls.fullSchedule;
  return <section className="shipping-dashboard shipping-packing-list">
    {live.warning && <Alert variant="warning" dismissible onClose={live.dismissWarning}>{live.warning}</Alert>}
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h2 className="h4 mb-0">Packing List Status</h2>
      <Button variant="outline-primary" disabled={busy} onClick={() => { loadWorkflow(); if (urls.fullSchedule) schedule.refetch(); }}>Refresh</Button>
    </div>
    <div className="d-flex flex-wrap gap-3 mb-3">
      <Form.Group controlId="packing-list-search" className="flex-grow-1"><Form.Label>Search</Form.Label><Form.Control value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search jobs, models, projects, locations, or notes" /></Form.Group>
      {[["AssemblyProjectStatus", "Assembly Status", assemblyStatus, setAssemblyStatus], ["PackingListStatus", "Packing List Status", packingStatus, setPackingStatus]].map(([key, label, value, setValue]) => <Form.Group key={key} controlId={`packing-list-${key}`}>
        <Form.Label>{label}</Form.Label><Form.Select value={value} onChange={(event) => setValue(event.target.value)}><option value="">All</option>{[...new Set(rows.map((row) => row[key]).filter(Boolean))].sort().map((option) => <option key={option} value={option}>{option}</option>)}</Form.Select>
      </Form.Group>)}
      <Form.Group controlId="packing-list-weeks" className="assembly-weeks-filter"><Form.Label>Weeks to show</Form.Label><Form.Select value={weeksToShow} onChange={(event) => setWeeksToShow(Number(event.target.value))}>{[1, 2, 3, 4, 6, 12].map((count) => <option key={count} value={count}>{count}</option>)}</Form.Select></Form.Group>
    </div>
    {failed ? <Alert variant="danger">{error || 'Unable to load project data. Refresh to retry.'}</Alert> : busy ? <p role="status">Loading packing list statuses…</p> : <>
      <p className="text-muted">{displayedCount} jobs</p>
      <Table responsive bordered hover size="sm" className="shipping-table shipping-packing-table">
        <thead><tr>{PACKING_LIST_COLUMNS.map(([key, label]) => <th scope="col" key={key}>{label}</th>)}</tr></thead>
        <tbody>{visibleWeeks.map((week) => {
          const definition = weekColumns[Number(week.replace('Week ', '')) - 1];
          return <React.Fragment key={week}>
            <tr className="shipping-packing-week"><td colSpan={PACKING_LIST_COLUMNS.length}><span className="fw-semibold me-3">{week}{definition ? ` · ${formatAssemblyDate(definition.startDate)} – ${formatAssemblyDate(definition.endDate)}` : ''}</span><span>{grouped[week].length} jobs</span></td></tr>
            {grouped[week].map((row) => <tr key={row.ProjectId}>{PACKING_LIST_COLUMNS.map(([key]) => <td key={key} className={key === 'AssemblyProjectStatus' ? getProjectStatusClass(row[key]) : key === 'PackingListStatus' ? getPackingListStatusClass(row[key]) : ''}>{key === 'CurrentPlanShipDate' ? formatAssemblyDate(row[key]) : row[key] || '—'}</td>)}</tr>)}
          </React.Fragment>;
        })}
          {!displayedCount && <tr><td colSpan={PACKING_LIST_COLUMNS.length} className="text-center text-muted">No jobs match the current filters.</td></tr>}
        </tbody>
      </Table>
    </>}
  </section>;
}
